import React from 'react';
import style from './style.module.scss';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTrendArrowIcon } from '~components/trend-arrow';

interface StatsBoxProps {
    title: string;
    currentValue: number;
    difference?: number;
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    isCurrency?: boolean;
    tooltipText?: string;
    suffix?: string;
}

const DifferenceDisplay: React.FC<{ difference?: number }> = ({
    difference,
}) => {
    const isDifferenceValid =
        difference !== undefined &&
        !isNaN(difference) &&
        difference !== Infinity;
    const isDifferencePositive = difference !== undefined && difference >= 0;

    const getArrow = (isDifferencePositive: boolean | undefined) => {
        return getTrendArrowIcon(isDifferencePositive, 32);
    };

    const sign = isDifferencePositive ? '+' : '-';
    const differenceClass = isDifferencePositive
        ? style.differenceUp
        : style.differenceDown;
    const arrowDirection = getArrow(isDifferencePositive);

    return isDifferenceValid ? (
        <div className={`${style.difference} ${differenceClass}`}>
            <span className={style.arrowCircle}>{arrowDirection}</span>
            {sign}
            {Math.abs(difference ?? 0).toFixed(2)}%
        </div>
    ) : null;
};

const abbreviateNumber = (num: number): { display: string; full: string } => {
    const full = num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    if (num >= 1000000) {
        const abbreviated = (num / 1000000).toFixed(1);
        return { display: `${abbreviated}M`, full };
    } else if (num >= 1000) {
        const abbreviated = (num / 1000).toFixed(1);
        return { display: `${abbreviated}k`, full };
    }
    return { display: full, full };
};

const formatNumber = (
    num: number,
    isCurrency?: boolean,
    suffix?: string
): { display: string; full: string } => {
    if (isCurrency) {
        const { display, full } = abbreviateNumber(num);
        return { display: `$${display}`, full: `$${full}` };
    }

    const value = num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    if (suffix) {
        return { display: `${value}${suffix}`, full: `${value}${suffix}` };
    }
    return { display: value, full: value };
};

const StatsBox: React.FC<StatsBoxProps> = ({
    title,
    currentValue,
    difference,
    icon: Icon,
    isCurrency,
    tooltipText,
    suffix,
}) => {
    const { display, full } = formatNumber(currentValue, isCurrency, suffix);
    const showTooltip = display !== full;

    return (
        <div className={style.statsBox}>
            <div className={style.title}>
                {Icon && (
                    <span className={style.icon_wrapper}>
                        <Icon />
                    </span>
                )}
                <span className={style.titleText}>
                    {title}
                    {tooltipText && (
                        <Tooltip title={tooltipText}>
                            <InfoCircleOutlined
                                style={{
                                    marginLeft: '0.5em',
                                    fontSize: '0.9em',
                                }}
                            />
                        </Tooltip>
                    )}
                </span>
            </div>
            {showTooltip ? (
                <Tooltip title={full}>
                    <div className={style.value}>{display}</div>
                </Tooltip>
            ) : (
                <div className={style.value}>{display}</div>
            )}
            <DifferenceDisplay difference={difference} />
        </div>
    );
};

export default StatsBox;
